import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import usePost from "../../Hooks/usePost";
import PageHeader from "../../Components/PageHeader/PageHeader";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
  const { token, isLoggedIn, loading: authLoading } = useAuth();
  const { postData, loading } = usePost();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    // Only redirect if not logged in AND we've finished loading auth state
    if (!authLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate, authLoading]);

  const validatePassword = (password) =>
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/.test(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setError("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("New passwords don't match");
      return;
    }
    if (!validatePassword(newPassword)) {
      setError(
        "Password must be at least 8 characters with uppercase, lowercase and a number"
      );
      return;
    }

    try {
      await postData(
        "/auth/change-password",
        { currentPassword, newPassword, confirmNewPassword },
        token
      );
      setSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (e) {
      setError(e.message || "Failed to change password");
    }
  };

  // Show loading indicator while auth state is being determined
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#003F75]"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not logged in (will redirect)
  if (!isLoggedIn) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Change Password"
        subtitle="Update your account password to keep your information secure"
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
                <div className="flex items-start gap-3">
                  <span className="text-lg">⚠️</span>
                  <span>{error}</span>
                </div>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-700">
                <div className="flex items-start gap-3">
                  <span className="text-lg">✅</span>
                  <span>{success}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#003F75] focus:border-transparent transition-all"
                  placeholder="Enter your current password"
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#003F75] focus:border-transparent transition-all"
                  placeholder="Enter a new password"
                />
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-500">
                    Password requirements:
                  </p>
                  <ul className="text-xs text-gray-500 ml-4 space-y-0.5">
                    <li className="list-disc">At least 8 characters long</li>
                    <li className="list-disc">One uppercase letter (A-Z)</li>
                    <li className="list-disc">One lowercase letter (a-z)</li>
                    <li className="list-disc">One number (0-9)</li>
                  </ul>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#003F75] focus:border-transparent transition-all"
                  placeholder="Confirm your new password"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 text-white bg-[#133B6E] rounded-lg hover:bg-[#0f2e55] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Changing Password...
                    </span>
                  ) : (
                    "Change Password"
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">Security Tips</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Use a unique password that you don't use elsewhere</li>
              <li>• Consider using a password manager</li>
              <li>• Change your password regularly</li>
              <li>• Never share your password with anyone</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
